import {ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, Injectable, signal, WritableSignal} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, FormGroupDirective, FormsModule, NgForm, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {AksjeeierbokId, Organisasjonsnummer, SkatteetatenAksjeeierbokRessursClient, SkatteetatenAksjeeierbokSammendragModell, SkatteetatenStatus} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatIconModule} from "@angular/material/icon";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {TranslateModule} from "@ngx-translate/core";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatError, MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {ErrorStateMatcher} from "@angular/material/core";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {StandardTekstPipe} from "felles-ng2/pipes/StandardTekstPipe";
import {TittelVerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {Temporal} from "temporal-polyfill";
import {lesPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {lagrerSignal} from "felles-ng2/tjenester/LagrerIndikator";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent');

export interface DialogParametre {
    aksjeeierbokId: AksjeeierbokId;
}

@Component({
    selector: 'opprett-aksjeeiere-fra-skatteetaten-orgnummer-dialog',
    templateUrl: 'opprett-aksjeeiere-fra-skatteetaten-orgnummer-dialog.html',
    styleUrls: ['opprett-aksjeeiere-fra-skatteetaten-orgnummer-dialog.scss'],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        TranslateModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatHint,
        MatError,
        InlineMeldingKomponent,
        FormaterPlainDatePipe,
        StandardTekstPipe,
        TittelVerdiKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent {

    static vis(dialog: MatDialog, aksjeeierbokId: AksjeeierbokId): MatDialogRef<OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent, SkatteetatenAksjeeierbokSammendragModell|null> {
        return dialog.open(OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent, {data: {aksjeeierbokId: aksjeeierbokId} as DialogParametre, disableClose: true, ariaLabelledBy: 'import-skatteetatet-tittel', width: '500px'});
    }
    private ab = new LukkAbonnementer();

    private aksjeeierbokId:AksjeeierbokId

    skjema = new FormGroup({organisasjonsnummer: new FormControl('', [...FeltValidatorer.norskOrganisasjonsnummer, Validators.required])})
    // gruppe: Signal<GruppeModell>;
    lagrer = lagrerSignal();
    sammendrag:WritableSignal<SkatteetatenAksjeeierbokSammendragModell|null> = signal(null);
    skatteetatenDato = computed(() => erIkkeNil(this.sammendrag()) ? lesPlainDateSerialisert(this.sammendrag()!.skatteetatenDato) : PlainDate.from('2023-12-31'));
    protected readonly orgnrUtenSpace = signal('');
    protected readonly kanImportere = computed(() => {
        return this.sammendrag()?.status === SkatteetatenStatus.OK;
    })


    // For å visse feil før man har gått ut av feltet -feks hvis orgnnummer ikke er gyldig, eller for mange tegn.
    matcher: ErrorStateMatcher = {
            isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
                return !!(control && control.invalid && (control.dirty || control.touched));
        }
    };

    constructor( @Inject(MAT_DIALOG_DATA) parametre: DialogParametre,private destroyRef: DestroyRef, private avhengigheter: OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponentAvhengigheter,
                public dialogRef: MatDialogRef<OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent>) {
        this.ab.registrerHosDestroyRef(this.destroyRef);
        this.aksjeeierbokId = parametre.aksjeeierbokId;
        this.ab.reg(this.skjema.controls.organisasjonsnummer.statusChanges
          .subscribe(() => {
              if (this.skjema.controls.organisasjonsnummer.invalid) {
                  this.sammendrag.set(null);
                  return;
              }
              this.hentSammendrag(this.skjema.controls.organisasjonsnummer.value!);
          }));
        this.ab.reg(this.skjema.controls.organisasjonsnummer.valueChanges
          .subscribe(() => {
              this.orgnrUtenSpace.set(this.skjema.controls.organisasjonsnummer.value!.toString().replace(/\s/g, ""));
          }));
    }

    avbryt(): void {
        this.dialogRef.close(null);
    }

    importerAksjeeierbok() {
        if (!this.kanImportere()) {
            // knapp skal ikke være tilgjengelig om sammendrag ikke er satt.
            throw new Error("Har ikke funnet organisasjon!");
        }
        this.dialogRef.close(this.sammendrag());
    }

    async hentSammendrag(organisasjonsnummer: Organisasjonsnummer) {
        const sammendrag = await this.lagrer.utfør(() =>
          this.avhengigheter.sammendrag(organisasjonsnummer)
        );
        this.sammendrag.set(sammendrag);
    }


    protected readonly erIkkeNil = erIkkeNil;
    protected readonly SkatteetatenStatus = SkatteetatenStatus;
}


@Injectable()
export class OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponentAvhengigheter {


    constructor(private skatteetatenAksjeeierbokRessurs: SkatteetatenAksjeeierbokRessursClient) {
    }

    sammendrag(orgnummer: Organisasjonsnummer): Promise<SkatteetatenAksjeeierbokSammendragModell> {
        return this.skatteetatenAksjeeierbokRessurs.sammendrag(orgnummer).somPromise();
    }

}
